import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ANALYTICS_EVENTS, CANCEL_REASONS, type CancelReason } from '@dunea/shared';
import { classNames } from '~/utils/classNames';
import { capture } from '~/lib/posthog';
import { pollBillingState } from '~/lib/billingRefresh';
import { Switch } from '~/components/ui/Switch';
import { PaymentMethodModal } from '~/components/account/PaymentMethodModal';
import { PlanCheckoutModal, PlanSwitchModal, serverMessage } from '~/components/account/PlanCheckoutModal';
import { useNotify } from '~/components/account/ModalNotifications';
import {
  apiGetBillingOverview,
  apiGetBillingConfig,
  apiGetInvoices,
  apiKeepCurrentPlan,
  apiCancelSubscription,
  apiResumeSubscription,
  apiSetAutoRenew,
  apiDownloadInvoicePdf,
  apiCreatePaymentMethodUpdateSession,
  type BillingOverview,
  type BillingConfig,
  type CancellationState,
  type InvoiceSummary,
  type PaymentMethodSummary,
} from '~/lib/api/billing';
import {
  ACCOUNT_PLANS_KEY,
  accountOverviewKey,
  apiGetAccountPlans,
  apiGetAccountPlanOverview,
  apiGetPlanInvoices,
  apiKeepPlan,
  apiPlanCardUpdate,
  apiSetPlanCancel,
  type AccountPlanOption,
  type AccountPlanOverview,
  type AccountPlansResponse,
  type PlanApp,
  type PlanInterval,
} from '~/lib/api/plans';
import {
  LEGACY_PLAN_NAMES,
  annualSavingsPercent,
  isHigherTier,
  plansAtInterval,
  formatCredits,
  formatMoney,
  planPrice,
  planScreenOf,
  type PlanScreen,
} from '~/lib/plans-model';
import { InviteBusinessCard } from '~/components/account/InviteBusinessCard';

/*
 * Billing: the plans Dunea sells — Free, Plus and Pro, on ONE credit balance
 * shared by the website, Dunea Desktop and Dunea Work — and the plan the
 * account is on now.
 *
 * Three states (planScreenOf):
 *  - free:   the plan grid, with Plus/Pro checkout.
 *  - plan:   Plus or Pro (bought here, in Dunea Desktop or in Dunea Work):
 *            manage it here — switch, cancel, card, invoices.
 *  - legacy: a website plan (Starter/Growth/Pro/Scale). No longer sold;
 *            subscribers keep it until they cancel and manage it here. Buying
 *            Plus/Pro on top is refused by the server, and its message is shown.
 *
 * Every price and allotment on this screen comes from the server (the plan
 * list, the overviews). There is no built-in catalog: if the plan list can't
 * load, the grid shows an error and a Retry, never a guessed number.
 */

// ─── Formatting + presentational primitives ───

// Status tinting as literal classes (text color + matching dot fill) for static extraction.
const STATUS_META: Record<string, { label: string; text: string; dot: string }> = {
  active: { label: 'Active', text: 'text-success', dot: 'bg-success' },
  trialing: { label: 'Trialing', text: 'text-info', dot: 'bg-info' },
  past_due: { label: 'Past due', text: 'text-warning', dot: 'bg-warning' },
  unpaid: { label: 'Unpaid', text: 'text-danger', dot: 'bg-danger' },
  incomplete: { label: 'Incomplete', text: 'text-warning', dot: 'bg-warning' },
  cancelled: { label: 'Cancelled', text: 'text-muted', dot: 'bg-muted' },
  on_hold: { label: 'On hold', text: 'text-warning', dot: 'bg-warning' },
  none: { label: 'Free', text: 'text-muted', dot: 'bg-muted' },
};

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function fmtMoney(amount: number | null, currency = 'usd'): string {
  if (amount == null) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const CARD_BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function fmtCardBrand(brand: string): string {
  const key = brand?.toLowerCase();
  return CARD_BRANDS[key] ?? (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card');
}

function fmtExpiry(pm: NonNullable<BillingOverview['paymentMethod']>): string {
  return `${String(pm.expMonth).padStart(2, '0')}/${String(pm.expYear).slice(-2)}`;
}

function fmtCycle(start: string | null, end: string | null): string {
  if (!start || !end) {
    return '—';
  }

  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  return `${new Date(start).toLocaleDateString('en-US', opts)} – ${new Date(end).toLocaleDateString('en-US', opts)}`;
}

// ─── Presentational primitives ───

function StatusBadge({ status, tier }: { status: string; tier: string }) {
  const meta = STATUS_META[status] ?? STATUS_META.none;
  const label = tier === 'free' && (status === 'none' || status === 'cancelled') ? 'Free' : meta.label;

  return (
    <span className={classNames('ui-pill uppercase tracking-[0.06em]', meta.text)}>
      <span className={classNames('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {label}
    </span>
  );
}

function Skeleton({
  height,
  width = '100%',
  radius = 8,
}: {
  height: number;
  width?: number | string;
  radius?: number;
}) {
  return <div className="animate-pulse bg-sunken" style={{ height, width, borderRadius: radius }} />;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="ui-caption mb-2.5 mt-6.5">{children}</div>;
}

const CARD_BOX = 'rounded-xl border border-hairline bg-raised';

function DetailRow({ label, value, last }: { label: string; value: ReactNode; last?: boolean }) {
  return (
    <div
      className={classNames(
        'flex min-h-[46px] items-center justify-between gap-4 py-3',
        !last && 'border-b border-hairline',
      )}
    >
      <span className="flex-shrink-0 text-[12.5px] text-muted">{label}</span>
      <div className="flex max-w-[64%] items-center justify-end text-right text-[13px] text-primary">{value}</div>
    </div>
  );
}

// ─── Billing history table ───

const HISTORY_COLS = '104px minmax(0,1fr) 92px 84px 116px';

const INVOICE_STATUS: Record<string, { label: string; text: string; dot: string }> = {
  paid: { label: 'Paid', text: 'text-success', dot: 'bg-success' },
  open: { label: 'Awaiting payment', text: 'text-warning', dot: 'bg-warning' },
  draft: { label: 'Draft', text: 'text-muted', dot: 'bg-muted' },
  void: { label: 'Voided', text: 'text-muted', dot: 'bg-muted' },
  uncollectible: { label: 'Unpaid', text: 'text-danger', dot: 'bg-danger' },
};

function InvoicePdfButton({ url, paymentId }: { url: string; paymentId: string }) {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (/^https?:\/\//.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    setBusy(true);

    try {
      await apiDownloadInvoicePdf(url, `invoice-${paymentId}.pdf`);
    } catch {
      notify.error('Could not download the invoice. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={busy}
      title="Download PDF"
      aria-label="Download invoice PDF"
      className="ui-btn-icon text-muted"
    >
      <span className="i-ph:download-simple text-[13px]" aria-hidden="true" />
    </button>
  );
}

function HistoryRow({ inv, first }: { inv: InvoiceSummary; first: boolean }) {
  const st = INVOICE_STATUS[inv.status ?? ''] ?? { label: 'Processing', text: 'text-muted', dot: 'bg-muted' };
  const description = inv.description ?? 'Subscription';
  const amount = fmtMoney(inv.amount, inv.currency);

  const status = (
    <span className={classNames('inline-flex items-center gap-1.5 text-[11px]', st.text)}>
      <span className={classNames('h-[5px] w-[5px] flex-shrink-0 rounded-full', st.dot)} />
      {st.label}
    </span>
  );

  const links = (
    <span className="inline-flex items-center justify-start gap-3">
      {inv.hostedInvoiceUrl ? (
        <a
          href={inv.hostedInvoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 whitespace-nowrap text-[11.5px] text-accent no-underline touch:min-h-10"
        >
          View invoice
          <span className="i-ph:arrow-up-right text-[11px]" />
        </a>
      ) : (
        <span className="text-[11.5px] text-faint">—</span>
      )}
      {inv.invoicePdf && <InvoicePdfButton url={inv.invoicePdf} paymentId={inv.id} />}
    </span>
  );

  return (
    <>
      {/* Phones: the five-column table has no room, so each invoice is a stacked card. */}
      <div
        className={classNames(
          'flex flex-col gap-1 bg-raised px-4 py-3 sm:hidden',
          !first && 'border-t border-hairline',
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] text-primary">
            {description}
          </span>
          <span className="flex-shrink-0 text-[12.5px] tabular-nums text-primary">{amount}</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3">
          <span className="inline-flex items-center gap-2.5">
            <span className="text-[12px] text-secondary">{fmtDate(inv.created)}</span>
            {status}
          </span>
          {links}
        </div>
      </div>

      <div
        className={classNames(
          'hidden items-center gap-3 bg-raised px-4 py-3 transition-colors duration-100 hover:bg-white/[0.02] sm:grid',
          !first && 'border-t border-hairline',
        )}
        style={{ gridTemplateColumns: HISTORY_COLS }}
      >
        <span className="text-[12px] text-secondary">{fmtDate(inv.created)}</span>
        <span
          title={description}
          className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] text-primary"
        >
          {description}
        </span>
        <span className="text-right text-[12.5px] tabular-nums text-primary">{amount}</span>
        {status}
        {links}
      </div>
    </>
  );
}

const CANCEL_REASON_LABELS: Record<CancelReason, string> = {
  too_expensive: 'Too expensive',
  not_using: 'Not using it enough',
  missing_feature: 'Missing a feature',
  quality: "Results weren't good enough",
  switching: 'Switching to another tool',
  other: 'Something else',
};

/** Optional "why are you leaving?" — never required to cancel. */
function CancelReasonPicker({
  reason,
  detail,
  onReason,
  onDetail,
}: {
  reason: CancelReason | null;
  detail: string;
  onReason: (reason: CancelReason) => void;
  onDetail: (detail: string) => void;
}) {
  return (
    <div className="mt-4">
      <div className="ui-label mb-2">What's the main reason? (optional)</div>
      <div className="flex flex-wrap gap-1.5">
        {CANCEL_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={reason === r}
            onClick={() => onReason(r)}
            className={classNames(
              'rounded-full border px-3 py-1 text-[12px] transition-colors',
              reason === r
                ? 'border-accent/40 bg-accent/10 text-primary'
                : 'border-subtle bg-raised text-secondary hover:text-primary',
            )}
          >
            {CANCEL_REASON_LABELS[r]}
          </button>
        ))}
      </div>
      {reason && (
        <textarea
          value={detail}
          onChange={(e) => onDetail(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder={reason === 'missing_feature' ? 'Which feature?' : 'Anything we could have done better?'}
          className="ui-input mt-2.5 w-full resize-none"
        />
      )}
    </div>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Keep subscription',
  danger,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && !busy && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[10050] bg-black/60 backdrop-blur-sm sm:backdrop-blur-md" />
        <RadixDialog.Content
          className="ui-root fixed left-1/2 top-1/2 z-[10051] max-h-[calc(100dvh-24px)] w-[480px] max-w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-subtle bg-surface px-5 pb-5 pt-6 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.85)] sm:px-6.5 sm:pt-6.5"
          onEscapeKeyDown={busy ? (e) => e.preventDefault() : undefined}
          onPointerDownOutside={busy ? (e) => e.preventDefault() : undefined}
        >
          {/* Icon + title */}
          <div className="mb-3.5 flex items-start gap-3.5">
            <div
              className={classNames(
                'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border',
                danger ? 'border-warning/25 bg-warning/10' : 'border-accent/20 bg-accent/10',
              )}
            >
              <span
                className={classNames(
                  danger ? 'i-ph:warning' : 'i-ph:info',
                  'text-[18px]',
                  danger ? 'text-warning' : 'text-accent',
                )}
              />
            </div>
            <div className="min-w-0 flex-1 pt-1">
              <RadixDialog.Title asChild>
                <h3 className="ui-serif m-0 text-[20px] tracking-[-0.01em] text-primary">{title}</h3>
              </RadixDialog.Title>
            </div>
          </div>

          {/* Body */}
          <RadixDialog.Description asChild>
            <div className="mb-5.5 text-[13px] leading-[1.6] text-secondary sm:pl-[50px]">{body}</div>
          </RadixDialog.Description>

          {/* Actions */}
          <div className="flex flex-wrap justify-end gap-2 border-t border-hairline pt-3.5">
            <button type="button" onClick={onClose} disabled={busy} className="ui-btn-ghost">
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={classNames(
                'inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[12.5px] font-medium tracking-[-0.005em] transition-colors duration-100 disabled:cursor-default disabled:opacity-60',
                danger
                  ? 'border-warning/40 bg-warning/10 text-warning hover:bg-warning/20'
                  : 'border-accent/35 bg-accent/10 text-accent hover:bg-accent/20',
              )}
            >
              {busy && <span className="i-ph:spinner-gap animate-spin text-[13px]" />}
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// ─── Plan grid ───

function BillingIntervalToggle({
  interval,
  onChange,
  savingsPercent,
}: {
  interval: PlanInterval;
  onChange: (i: PlanInterval) => void;
  savingsPercent: number | null;
}) {
  const OPTIONS: { value: PlanInterval; label: string }[] = [
    { value: 'month', label: 'Monthly' },
    { value: 'year', label: savingsPercent != null ? `Annual · Save ${savingsPercent}%` : 'Annual' },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Billing interval"
      className="inline-flex items-center gap-0.5 rounded-full border border-hairline bg-sunken p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = interval === opt.value;

        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={classNames(
              'whitespace-nowrap rounded-full px-3.5 py-1 text-[12px] font-medium tracking-[-0.005em] transition-colors duration-100 touch:min-h-9',
              active ? 'bg-accent/15 text-accent' : 'text-muted hover:text-secondary',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PlanBadge({ label, tone }: { label: string; tone: 'accent' | 'warning' }) {
  return (
    <span
      className={classNames(
        'ui-caption absolute -top-2 right-2.5 rounded-full border bg-sunken px-2 py-0.5 text-[9px]',
        tone === 'accent' ? 'text-accent border-accent/30' : 'text-warning border-warning/30',
      )}
    >
      {label}
    </span>
  );
}

/**
 * What each plan adds on the website, under its credits. Website copy (the
 * server's plan features describe Dunea Code); each paid plan lists what it
 * adds on top of the one before it.
 */
const WEBSITE_PLAN_POINTS: Record<string, { lead?: string; points: string[] }> = {
  free: { points: ['Build and preview sites', 'Unlimited projects', 'Works in Dunea Code too'] },
  plus: {
    lead: 'Everything in Free, plus:',
    points: ['Publish unlimited sites', 'Custom domains + SSL', 'Remove the Dunea badge', 'Buy extra credits anytime'],
  },
  pro: { lead: 'Everything in Plus, plus:', points: ['More credits per dollar', 'For building every day'] },
  max: { lead: 'Everything in Pro, plus:', points: ['The most credits per dollar', 'Billed monthly only'] },
};

function PlanGridCard({
  plan,
  interval,
  isCurrent,
  isScheduled,
  action,
}: {
  plan: AccountPlanOption;
  interval: PlanInterval;
  isCurrent: boolean;
  isScheduled: boolean;
  action: { label: string; primary: boolean; onClick: () => void; disabled?: boolean } | null;
}) {
  const isFree = plan.tier === 'free';
  const price = planPrice(plan, interval);

  return (
    <div
      className={classNames(
        'relative flex flex-col rounded-xl border bg-raised px-4 pb-3.5 pt-4.5 transition-transform duration-100',
        isCurrent
          ? 'border-accent'
          : isScheduled
            ? 'border-warning/40'
            : plan.badge
              ? 'border-accent/30'
              : 'border-hairline',
        !isCurrent && !isScheduled && 'can-hover:hover:-translate-y-px hover:border-subtle',
      )}
    >
      {isScheduled ? (
        <PlanBadge label="Scheduled" tone="warning" />
      ) : isCurrent ? (
        <PlanBadge label="Current" tone="accent" />
      ) : plan.badge ? (
        <PlanBadge label={plan.badge} tone="accent" />
      ) : null}

      <div className="text-[12.5px] font-medium tracking-[-0.005em] text-primary">{plan.name}</div>
      <div className="my-0.5 flex items-baseline gap-1">
        <span className="ui-serif text-[26px] leading-none">
          {price ? formatMoney(price.value, price.currency) : '—'}
        </span>
        {!isFree && <span className="text-[13px] text-muted">{interval === 'year' ? '/yr' : '/mo'}</span>}
      </div>
      <div className="text-[11.5px] text-muted">
        {isFree ? `${formatCredits(plan.credits)} credits, once` : `${formatCredits(plan.credits)} credits/mo`}
      </div>

      {WEBSITE_PLAN_POINTS[plan.tier] && (
        <div className="mt-3.5 border-t border-hairline pt-3">
          {WEBSITE_PLAN_POINTS[plan.tier].lead && (
            <div className="mb-1.5 text-[11px] font-medium text-secondary">{WEBSITE_PLAN_POINTS[plan.tier].lead}</div>
          )}
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {WEBSITE_PLAN_POINTS[plan.tier].points.map((point) => (
              <li key={point} className="flex items-start gap-1.5 text-[11.5px] leading-snug text-secondary">
                <span className="i-ph:check mt-0.5 shrink-0 text-[11px] text-accent" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="min-h-3 flex-1" />

      <div className="mt-2">
        {isCurrent ? (
          <div className="inline-flex items-center gap-1.5 py-1.5 text-[11.5px] font-medium text-accent">
            <span className="i-ph:check-circle-fill text-[13px]" />
            Your plan
          </div>
        ) : isScheduled ? (
          <div className="inline-flex items-center gap-1.5 py-1.5 text-[11.5px] font-medium text-warning">
            <span className="i-ph:calendar-check text-[13px]" />
            Scheduled
          </div>
        ) : action ? (
          <button
            onClick={action.onClick}
            disabled={action.disabled}
            className={classNames('w-full justify-center', action.primary ? 'ui-btn-accent' : 'ui-btn-secondary')}
          >
            {action.label}
          </button>
        ) : (
          <div className="h-[30px]" />
        )}
      </div>
    </div>
  );
}

function PlansGridError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={classNames(CARD_BOX, 'border-danger/30 px-4.5 py-5.5 text-center')}>
      <div className="mb-1 text-[12.5px] text-primary">Couldn't load plans and prices</div>
      <div className="mb-3 text-[11.5px] text-muted">
        Nothing can be bought until they load. This is usually temporary.
      </div>
      <button onClick={onRetry} className="ui-btn-secondary">
        Retry
      </button>
    </div>
  );
}

// ─── Payment method + invoices (shared by the legacy and Plus/Pro views) ───

function PaymentMethodBlock({
  paymentMethod,
  billingEmail,
  warning,
  action,
  footer,
}: {
  paymentMethod: PaymentMethodSummary | null;
  billingEmail: string | null;
  warning?: ReactNode;
  action: { label: string; busyLabel: string; busy: boolean; onClick: () => void } | null;
  footer?: ReactNode;
}) {
  return (
    <>
      <SectionLabel>Payment Method</SectionLabel>
      {warning && <div className="mb-2 text-[12px] leading-normal text-warning">{warning}</div>}
      <div className={classNames(CARD_BOX, 'px-4.5 py-1')}>
        {paymentMethod ? (
          <div className="flex flex-wrap items-center gap-3.5 border-b border-hairline py-3.5">
            <div className="flex h-8 w-[46px] flex-shrink-0 items-center justify-center rounded-md border border-hairline bg-sunken">
              <span className="i-ph:credit-card text-[18px] text-accent" />
            </div>
            <div className="min-w-0">
              <div className="text-[14.5px] font-medium text-primary">
                {fmtCardBrand(paymentMethod.brand)} •••• {paymentMethod.last4}
              </div>
              <div className="mt-0.5 text-[12px] text-muted">Expires {fmtExpiry(paymentMethod)}</div>
            </div>
            <div className="flex-1" />
            {action && (
              <button onClick={action.onClick} disabled={action.busy} className="ui-btn-secondary">
                {action.busy ? action.busyLabel : action.label}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 border-b border-hairline pb-6 pt-6.5 text-center">
            <span className="i-ph:credit-card text-[26px] text-faint" />
            <div className="text-[13px] text-secondary">No payment method on file</div>
            <div className="max-w-[240px] text-[11.5px] text-muted">Add a card to keep your subscription active.</div>
            {action && (
              <button onClick={action.onClick} disabled={action.busy} className="ui-btn-accent mt-1">
                {action.busy ? action.busyLabel : 'Add Card'}
              </button>
            )}
          </div>
        )}
        <DetailRow label="Billing Email" value={billingEmail ?? '—'} last />
      </div>
      {footer}
    </>
  );
}

function InvoiceHistory({
  invoices,
  loading,
  error,
  onRetry,
}: {
  invoices: InvoiceSummary[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  return (
    <>
      <SectionLabel>Billing History</SectionLabel>
      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      ) : error ? (
        <div className={classNames(CARD_BOX, 'border-danger/30 px-4.5 py-5.5 text-center')}>
          <div className="mb-1 text-[12.5px] text-primary">Couldn't load your billing history</div>
          <div className="mb-3 text-[11.5px] text-muted">This is usually temporary.</div>
          <button onClick={onRetry} className="ui-btn-secondary">
            Retry
          </button>
        </div>
      ) : invoices.length === 0 ? (
        <div className={classNames(CARD_BOX, 'px-4.5 py-6 text-center text-[12.5px] text-muted')}>
          No invoices yet — they'll appear here after your first payment.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-hairline">
          <div
            className="hidden gap-3 border-b border-hairline bg-sunken px-4 py-2.5 sm:grid"
            style={{ gridTemplateColumns: HISTORY_COLS }}
          >
            {['Date', 'Description', 'Amount', 'Status', 'Invoice'].map((h, i) => (
              <span
                key={h}
                className={classNames(
                  'text-[9.5px] font-medium uppercase tracking-[0.07em] text-muted',
                  i === 2 ? 'text-right' : 'text-left',
                )}
              >
                {h}
              </span>
            ))}
          </div>
          {invoices.map((inv, i) => (
            <HistoryRow key={inv.id} inv={inv} first={i === 0} />
          ))}
        </div>
      )}
    </>
  );
}

/** Drop "open" invoices superseded by a "paid" one on the same day for the same amount. */
function cleanInvoices(raw: InvoiceSummary[]): InvoiceSummary[] {
  const dayKey = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
  const paid = new Set(raw.filter((i) => i.status === 'paid').map((i) => `${dayKey(i.created)}:${i.amount}`));

  return raw.filter((inv) => inv.status !== 'open' || !paid.has(`${dayKey(inv.created)}:${inv.amount}`));
}

// ─── Current plan: a grandfathered website plan ───

type LegacyConfirmation = { kind: 'cancel' } | { kind: 'auto-renew-off' };

function LegacyPlanSection({
  overview,
  config,
  configError,
}: {
  overview: BillingOverview;
  config?: BillingConfig;
  configError: boolean;
}) {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [confirmation, setConfirmation] = useState<LegacyConfirmation | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const tier = overview.activePlan ?? overview.tier;
  const status = overview.status;
  const name = LEGACY_PLAN_NAMES[tier] ?? tier;
  const isSubscribed = tier !== 'free' && (status === 'active' || status === 'past_due' || status === 'trialing');
  const isStripe = (config?.provider ?? 'stripe') === 'stripe';
  // Dodo moves a failed renewal to free but keeps the subscription; a new card on it recovers the plan.
  const paymentFailed =
    !isStripe && (status === 'past_due' || status === 'on_hold') && Boolean(overview.subscriptionId);
  const canUpdateCard = isStripe && Boolean(config?.publishableKey);
  const canRedirectCard = !isStripe && (isSubscribed || paymentFailed);
  const isAnnual = overview.interval === 'year';
  const scheduledName = overview.scheduledPlan
    ? (LEGACY_PLAN_NAMES[overview.scheduledPlan] ?? overview.scheduledPlan)
    : null;
  const cancelDate = overview.cancelAt ?? overview.currentPeriodEnd;

  const invoicesQuery = useQuery<InvoiceSummary[]>({
    queryKey: ['invoices'],
    queryFn: apiGetInvoices,
    staleTime: 30_000,
  });
  const invoices = useMemo(() => cleanInvoices(invoicesQuery.data ?? []), [invoicesQuery.data]);

  const reconcile = (patch: Partial<BillingOverview>, isSettled?: (o: BillingOverview) => boolean) => {
    queryClient.setQueryData<BillingOverview>(['billing-overview'], (old) => (old ? { ...old, ...patch } : old));

    const delays = [400, 1200, 2500, 5000];
    let attempt = 0;
    const poll = async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing-overview'] });

      const fresh = queryClient.getQueryData<BillingOverview>(['billing-overview']);
      const settled = !isSettled || (fresh ? isSettled(fresh) : false);

      if (settled || attempt >= delays.length) {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        return;
      }

      window.setTimeout(poll, delays[attempt++]);
    };
    window.setTimeout(poll, delays[attempt++]);
  };

  const cardRedirectMutation = useMutation({
    mutationFn: apiCreatePaymentMethodUpdateSession,
    onSuccess: ({ url }) => {
      if (url) {
        window.location.href = url;
      } else {
        notify.error("Card management isn't available here. Reach us on Discord: https://discord.gg/mCdjW3Ztv8");
      }
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not update your card. Please try again.')),
  });

  const keepMutation = useMutation({
    mutationFn: apiKeepCurrentPlan,
    onSuccess: () => {
      reconcile({ scheduledPlan: null, effectiveDate: null }, (o) => o.scheduledPlan === null);
      notify.success('Scheduled downgrade cancelled');
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not cancel the scheduled change')),
  });

  const cancelMutation = useMutation({
    mutationFn: apiCancelSubscription,
    onSuccess: (data: CancellationState) => {
      reconcile({ cancelAtPeriodEnd: true, autoRenew: false, cancelAt: data.cancelAt }, (o) => o.cancelAtPeriodEnd);
      notify.warning('Subscription set to cancel at period end');
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not cancel your subscription')),
  });

  const resumeMutation = useMutation({
    mutationFn: apiResumeSubscription,
    onSuccess: () => {
      reconcile({ cancelAtPeriodEnd: false, autoRenew: true, cancelAt: null }, (o) => !o.cancelAtPeriodEnd);
      notify.success('Subscription resumed');
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not resume your subscription')),
  });

  const autoRenewMutation = useMutation({
    mutationFn: (enabled: boolean) => apiSetAutoRenew(enabled),
    onSuccess: (data: CancellationState, enabled) => {
      reconcile(
        { autoRenew: enabled, cancelAtPeriodEnd: !enabled, cancelAt: data.cancelAt },
        (o) => o.autoRenew === enabled,
      );

      if (enabled) {
        notify.success('Auto-renewal turned on');
      } else {
        notify.warning('Auto-renewal off — plan cancels at period end');
      }
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not update auto-renewal')),
  });

  const busy =
    keepMutation.isPending || cancelMutation.isPending || resumeMutation.isPending || autoRenewMutation.isPending;

  const runConfirmation = () => {
    if (confirmation?.kind === 'cancel') {
      cancelMutation.mutate();
    } else if (confirmation?.kind === 'auto-renew-off') {
      autoRenewMutation.mutate(false);
    }

    setConfirmation(null);
  };

  return (
    <>
      <div className="rounded-xl border border-accent/20 bg-raised px-5.5 py-5">
        <div className="flex items-center gap-2.5">
          <span className="ui-caption text-accent">Current plan</span>
          <div className="flex-1" />
          {isSubscribed &&
            (overview.cancelAtPeriodEnd ? (
              <button onClick={() => resumeMutation.mutate()} disabled={busy} className="ui-btn-accent">
                {resumeMutation.isPending ? 'Resuming…' : 'Resume subscription'}
              </button>
            ) : (
              <button
                onClick={() => setConfirmation({ kind: 'cancel' })}
                disabled={busy}
                className="ui-btn-ghost text-muted"
              >
                Cancel subscription
              </button>
            ))}
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
          <span className="text-[19px] font-semibold tracking-[-0.01em] text-primary">{name}</span>
          <StatusBadge status={status} tier={tier} />
          <span className="ui-pill uppercase tracking-[0.06em] text-warning">Legacy plan</span>
        </div>

        {overview.priceMonthly != null && (
          <div className="mt-3.5 flex items-baseline gap-1.5">
            <span className="ui-serif text-[38px] leading-none tracking-[-0.015em]">
              {fmtMoney(overview.priceMonthly, overview.currency)}
            </span>
            <span className="text-[13px] text-muted">{isAnnual ? '/year' : '/month'}</span>
          </div>
        )}
        {tier !== 'free' && (
          <div className="mt-1.5 text-[12.5px] text-secondary">
            {formatCredits(overview.creditsAllocated)} credits / month
            {isAnnual && <span className="text-muted"> · billed annually</span>}
          </div>
        )}

        <div className="mt-3 text-[12px] leading-normal text-secondary">
          {name} is a website plan we no longer sell. You keep it until you cancel, and its credits refill the one
          balance you use on the website builder and in Dunea Code. To move to Plus or Pro, cancel {name} first; once it
          ends you can choose either below.
        </div>

        {isSubscribed && (
          <div className="mt-4 border-t border-accent/10 pt-3.5">
            {overview.cancelAtPeriodEnd ? (
              <div className="text-[12.5px] leading-normal text-warning">
                <span className="i-ph:warning-circle mr-1.5 align-[-2px]" />
                Cancels on <b>{fmtDate(cancelDate)}</b> — you keep {name} until then.
                <span className="text-muted"> Resume anytime.</span>
              </div>
            ) : overview.scheduledPlan ? null : isAnnual ? (
              <div className="text-[12.5px] text-secondary">
                <span className="i-ph:arrows-clockwise mr-1.5 align-[-2px] text-muted" />
                Credits refresh monthly
                <span className="text-muted">
                  {' '}
                  · next payment <b className="text-primary">{fmtDate(overview.currentPeriodEnd)}</b>
                </span>
              </div>
            ) : (
              <div className="text-[12.5px] text-secondary">
                <span className="i-ph:arrows-clockwise mr-1.5 align-[-2px] text-muted" />
                Next renewal <b className="text-primary">{fmtDate(overview.currentPeriodEnd)}</b>
                <span className="text-muted"> · {formatCredits(overview.creditsAllocated)} credits refresh</span>
              </div>
            )}
          </div>
        )}
      </div>

      {isSubscribed && scheduledName && (
        <div className="mt-3 rounded-xl border border-warning/30 bg-warning/[0.06] px-5.5 py-5">
          <div className="flex items-center gap-2">
            <span className="i-ph:calendar-dots text-[15px] text-warning" />
            <span className="ui-caption text-warning">Scheduled downgrade</span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-x-6">
            <div>
              <div className="ui-caption tracking-[0.07em]">New Plan</div>
              <div className="mt-1 text-[18px] font-semibold text-primary">{scheduledName}</div>
            </div>
            <div>
              <div className="ui-caption tracking-[0.07em]">Effective</div>
              <div className="mt-1.5 text-[15px] text-primary">{fmtDate(overview.effectiveDate)}</div>
            </div>
            <button
              onClick={() => keepMutation.mutate()}
              disabled={busy}
              className="ui-btn-accent w-full justify-center sm:w-auto sm:justify-self-end"
            >
              {keepMutation.isPending ? 'Keeping…' : `Keep ${name}`}
            </button>
          </div>
        </div>
      )}

      {(isSubscribed || paymentFailed) && (
        <PaymentMethodBlock
          paymentMethod={overview.paymentMethod}
          billingEmail={overview.billingEmail}
          warning={
            paymentFailed ? 'Your last renewal didn’t go through. Add a working card to get your plan back.' : undefined
          }
          action={
            canUpdateCard
              ? { label: 'Update Card', busyLabel: 'Update Card', busy, onClick: () => setShowPaymentModal(true) }
              : canRedirectCard
                ? {
                    label: 'Update Card',
                    busyLabel: 'Opening…',
                    busy: cardRedirectMutation.isPending,
                    onClick: () => cardRedirectMutation.mutate(),
                  }
                : null
          }
          footer={
            configError ? (
              <div className="mt-2 text-[11.5px] leading-normal text-warning">
                <span className="i-ph:warning-circle mr-1.5 align-[-2px]" />
                Card management is temporarily unavailable. Please try again shortly.
              </div>
            ) : null
          }
        />
      )}

      {isSubscribed && (
        <>
          <SectionLabel>Subscription Details</SectionLabel>
          <div className={classNames(CARD_BOX, 'px-4.5 py-0.5')}>
            <DetailRow label="Status" value={<StatusBadge status={status} tier={tier} />} />
            <DetailRow label="Started" value={fmtDate(overview.startedAt)} />
            <DetailRow label="Billing Cycle" value={fmtCycle(overview.currentPeriodStart, overview.currentPeriodEnd)} />
            <DetailRow label="Next Renewal" value={overview.autoRenew ? fmtDate(overview.currentPeriodEnd) : '—'} />
            <DetailRow
              label="Auto Renewal"
              last
              value={
                <span className="inline-flex items-center gap-2.5">
                  <span className={classNames('text-[12.5px]', overview.autoRenew ? 'text-success' : 'text-muted')}>
                    {overview.autoRenew ? 'Enabled' : 'Disabled'}
                  </span>
                  <Switch
                    checked={overview.autoRenew}
                    disabled={busy}
                    aria-label="Auto renewal"
                    onCheckedChange={(enabled) =>
                      enabled ? autoRenewMutation.mutate(true) : setConfirmation({ kind: 'auto-renew-off' })
                    }
                  />
                </span>
              }
            />
          </div>
        </>
      )}

      <InvoiceHistory
        invoices={invoices}
        loading={invoicesQuery.isLoading}
        error={invoicesQuery.isError}
        onRetry={() => invoicesQuery.refetch()}
      />

      {showPaymentModal && config?.publishableKey && (
        <PaymentMethodModal
          publishableKey={config.publishableKey}
          hasExistingCard={Boolean(overview.paymentMethod)}
          onClose={() => setShowPaymentModal(false)}
        />
      )}

      {confirmation?.kind === 'cancel' && (
        <ConfirmDialog
          title="Cancel subscription?"
          danger
          confirmLabel="Yes, cancel"
          busy={cancelMutation.isPending}
          onConfirm={runConfirmation}
          onClose={() => setConfirmation(null)}
          body={
            <>
              You'll keep <b>{name}</b> until <b>{fmtDate(overview.currentPeriodEnd)}</b>, then your account moves to
              Free. Credits you bought stay in your balance. {name} is no longer sold, so once it ends you can't come
              back to it, but you can choose Plus or Pro.
            </>
          }
        />
      )}
      {confirmation?.kind === 'auto-renew-off' && (
        <ConfirmDialog
          title="Turn off auto-renewal?"
          confirmLabel="Turn off"
          danger
          busy={autoRenewMutation.isPending}
          onConfirm={runConfirmation}
          onClose={() => setConfirmation(null)}
          body={
            <>
              Turning off auto-renewal cancels your subscription at the end of the current billing period. It stays
              active until <b>{fmtDate(overview.currentPeriodEnd)}</b>. {name} is no longer sold, so once it ends you
              can't come back to it.
            </>
          }
        />
      )}
    </>
  );
}

// ─── Current plan: Plus or Pro ───

function AccountPlanSection({
  app,
  account,
  plans,
}: {
  app: PlanApp;
  account: AccountPlanOverview;
  plans?: AccountPlansResponse;
}) {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState<CancelReason | null>(null);
  const [cancelDetail, setCancelDetail] = useState('');

  const plan = plans?.plans.find((p) => p.tier === account.tier);
  const name = plan?.name ?? account.tier.charAt(0).toUpperCase() + account.tier.slice(1);
  const scheduledName = account.scheduledPlan
    ? (plans?.plans.find((p) => p.tier === account.scheduledPlan)?.name ?? account.scheduledPlan)
    : null;
  const isAnnual = account.interval === 'year';
  const paymentFailed = account.status === 'past_due' || account.status === 'on_hold';
  // the plan's price per period: Dodo's live figure, else the server's catalog price for this cadence
  const listPrice = plan ? planPrice(plan, account.interval) : null;
  const price = account.priceMonthly != null ? { value: account.priceMonthly, currency: account.currency } : listPrice;

  const invoicesQuery = useQuery<InvoiceSummary[]>({
    queryKey: ['account-plan-invoices', app],
    queryFn: () => apiGetPlanInvoices(app),
    staleTime: 30_000,
  });
  const invoices = useMemo(() => cleanInvoices(invoicesQuery.data ?? []), [invoicesQuery.data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['account-plan-overview'] });
    pollBillingState(queryClient);
  };

  const cancelMutation = useMutation({
    mutationFn: (cancel: boolean) =>
      apiSetPlanCancel(cancel, app, cancel ? { reason: cancelReason ?? undefined, detail: cancelDetail } : undefined),
    onSuccess: (data) => {
      queryClient.setQueryData<AccountPlanOverview>(accountOverviewKey(app), (old) =>
        old ? { ...old, cancelAtPeriodEnd: data.cancelAtPeriodEnd } : old,
      );
      refresh();

      if (data.cancelAtPeriodEnd) {
        notify.warning(`${name} will end on ${fmtDate(data.currentPeriodEnd ?? account.currentPeriodEnd)}`);
      } else {
        notify.success(`${name} will renew`);
      }
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not update your plan. Please try again.')),
  });

  const keepMutation = useMutation({
    mutationFn: () => apiKeepPlan(app),
    onSuccess: () => {
      refresh();
      notify.success('Scheduled change cancelled');
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not cancel the scheduled change')),
  });

  const cardMutation = useMutation({
    mutationFn: () => apiPlanCardUpdate(app),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err) => notify.error(serverMessage(err, 'Could not update your card. Please try again.')),
  });

  const busy = cancelMutation.isPending || keepMutation.isPending;

  return (
    <>
      <div className="rounded-xl border border-accent/20 bg-raised px-5.5 py-5">
        <div className="flex items-center gap-2.5">
          <span className="ui-caption text-accent">Current plan</span>
          <div className="flex-1" />
          {account.subscriptionId &&
            (account.cancelAtPeriodEnd ? (
              <button onClick={() => cancelMutation.mutate(false)} disabled={busy} className="ui-btn-accent">
                {cancelMutation.isPending ? 'Resuming…' : 'Resume plan'}
              </button>
            ) : (
              <button onClick={() => setConfirmCancel(true)} disabled={busy} className="ui-btn-ghost text-muted">
                Cancel plan
              </button>
            ))}
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
          <span className="text-[19px] font-semibold tracking-[-0.01em] text-primary">{name}</span>
          <StatusBadge status={account.status} tier={account.tier} />
        </div>

        {price && (
          <div className="mt-3.5 flex items-baseline gap-1.5">
            <span className="ui-serif text-[38px] leading-none tracking-[-0.015em]">
              {formatMoney(price.value, price.currency)}
            </span>
            <span className="text-[13px] text-muted">{isAnnual ? '/year' : '/month'}</span>
          </div>
        )}
        <div className="mt-1.5 text-[12.5px] text-secondary">
          {formatCredits(account.plan.creditsAllocated)} credits / month
          {isAnnual && <span className="text-muted"> · billed annually</span>}
        </div>
        <div className="mt-3 text-[12px] leading-normal text-secondary">
          {app === 'work' ? 'Bought in Dunea Work. ' : ''}Pay once, use it on both: one plan and one credit balance for
          the website builder and Dunea Code.
        </div>

        <div className="mt-4 border-t border-accent/10 pt-3.5">
          {account.cancelAtPeriodEnd ? (
            <div className="text-[12.5px] leading-normal text-warning">
              <span className="i-ph:warning-circle mr-1.5 align-[-2px]" />
              Ends on <b>{fmtDate(account.currentPeriodEnd)}</b> — you keep {name} until then.
              <span className="text-muted"> Resume anytime.</span>
            </div>
          ) : scheduledName ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-warning">
              <span>
                <span className="i-ph:calendar-dots mr-1.5 align-[-2px]" />
                Moves to <b>{scheduledName}</b> on {fmtDate(account.effectiveDate)}.
              </span>
              <button onClick={() => keepMutation.mutate()} disabled={busy} className="ui-btn-secondary">
                {keepMutation.isPending ? 'Keeping…' : `Keep ${name}`}
              </button>
            </div>
          ) : (
            <div className="text-[12.5px] text-secondary">
              <span className="i-ph:arrows-clockwise mr-1.5 align-[-2px] text-muted" />
              {isAnnual ? 'Credits refresh monthly · next payment ' : 'Next renewal '}
              <b className="text-primary">{fmtDate(account.currentPeriodEnd)}</b>
            </div>
          )}
        </div>
      </div>

      <PaymentMethodBlock
        paymentMethod={account.paymentMethod}
        billingEmail={account.billingEmail}
        warning={
          paymentFailed ? 'Your last renewal didn’t go through. Add a working card to keep your plan.' : undefined
        }
        action={
          account.subscriptionId
            ? {
                label: 'Update Card',
                busyLabel: 'Opening…',
                busy: cardMutation.isPending,
                onClick: () => cardMutation.mutate(),
              }
            : null
        }
      />

      <InvoiceHistory
        invoices={invoices}
        loading={invoicesQuery.isLoading}
        error={invoicesQuery.isError}
        onRetry={() => invoicesQuery.refetch()}
      />

      {confirmCancel && (
        <ConfirmDialog
          title={`Cancel ${name}?`}
          danger
          confirmLabel="Yes, cancel"
          cancelLabel={`Keep ${name}`}
          busy={cancelMutation.isPending}
          onConfirm={() => {
            cancelMutation.mutate(true);
            setConfirmCancel(false);
          }}
          onClose={() => setConfirmCancel(false)}
          body={
            <>
              You'll keep <b>{name}</b> until <b>{fmtDate(account.currentPeriodEnd)}</b>, then your account moves to
              Free. Credits you bought stay in your balance. You can resume any time before then.
              <CancelReasonPicker
                reason={cancelReason}
                detail={cancelDetail}
                onReason={setCancelReason}
                onDetail={setCancelDetail}
              />
            </>
          }
        />
      )}
    </>
  );
}

// ─── Current plan: Free ───

function FreePlanCard({ freePlan }: { freePlan?: AccountPlanOption }) {
  return (
    <div className="rounded-xl border border-accent/20 bg-raised px-5.5 py-5">
      <span className="ui-caption text-accent">Current plan</span>
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        <span className="text-[19px] font-semibold tracking-[-0.01em] text-primary">Free</span>
        <StatusBadge status="none" tier="free" />
      </div>
      <div className="mt-3 text-[12px] leading-normal text-secondary">
        {freePlan ? `${formatCredits(freePlan.credits)} credits to start, once. ` : ''}Pay once, use it on both: one
        plan and one credit balance for the website builder and Dunea Code. Choose Plus or Pro for monthly credits,
        publishing and custom domains.
      </div>
    </div>
  );
}

// ─── Main ───

type PlanDialog =
  | { kind: 'checkout'; plan: AccountPlanOption; interval: PlanInterval }
  | { kind: 'switch'; plan: AccountPlanOption; interval: PlanInterval };

export function PlansTab() {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [dialog, setDialog] = useState<PlanDialog | null>(null);
  const [billingInterval, setBillingInterval] = useState<PlanInterval>('month');

  const overviewQuery = useQuery<BillingOverview>({
    queryKey: ['billing-overview'],
    queryFn: apiGetBillingOverview,
    staleTime: 30_000,
  });
  const overview = overviewQuery.data;

  const { data: config, isError: configError } = useQuery<BillingConfig>({
    queryKey: ['billing-config'],
    queryFn: apiGetBillingConfig,
    staleTime: Infinity,
  });

  const plansQuery = useQuery<AccountPlansResponse>({
    queryKey: ACCOUNT_PLANS_KEY,
    queryFn: apiGetAccountPlans,
    staleTime: 60 * 60 * 1000,
  });
  const plans = plansQuery.data;

  const { screen, app }: { screen: PlanScreen; app: PlanApp } = overview
    ? planScreenOf(overview)
    : { screen: 'free', app: 'code' };

  const accountQuery = useQuery<AccountPlanOverview>({
    queryKey: accountOverviewKey(app),
    queryFn: () => apiGetAccountPlanOverview(app),
    enabled: screen === 'plan',
    staleTime: 30_000,
  });
  const account = accountQuery.data;

  // Open the toggle on the current plan's cadence once, so an annual subscriber sees annual prices.
  const intervalInitedRef = useRef(false);
  useEffect(() => {
    if (intervalInitedRef.current || !overview) {
      return;
    }

    if (screen === 'plan' && account) {
      setBillingInterval(account.interval);
      intervalInitedRef.current = true;
    } else if (screen !== 'plan') {
      setBillingInterval(overview.interval === 'year' ? 'year' : 'month');
      intervalInitedRef.current = true;
    }
  }, [overview, account, screen]);

  const savingsPercent = plans ? annualSavingsPercent(plans.plans) : null;

  if (overviewQuery.isLoading || (screen === 'plan' && accountQuery.isLoading)) {
    return (
      <div>
        <h3 className="ui-serif m-0 mb-4.5 text-[24px]">Billing</h3>
        <Skeleton height={116} radius={12} />
        <div className="h-3" />
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </div>
      </div>
    );
  }

  if (overviewQuery.isError || !overview || (screen === 'plan' && (accountQuery.isError || !account))) {
    return (
      <div>
        <h3 className="ui-serif m-0 mb-4.5 text-[24px]">Billing</h3>
        <div className="rounded-xl border border-danger/30 bg-danger/[0.07] px-5 py-7 text-center">
          <div className="mb-1 text-[13px] text-primary">Couldn't load billing</div>
          <div className="mb-3.5 text-[12px] text-muted">Something went wrong fetching your plan.</div>
          <button
            onClick={() => {
              overviewQuery.refetch();
              accountQuery.refetch();
            }}
            className="ui-btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentName =
    screen === 'plan' && account ? (plans?.plans.find((p) => p.tier === account.tier)?.name ?? account.tier) : 'Free';

  const actionFor = (plan: AccountPlanOption) => {
    if (plan.tier === 'free') {
      return null;
    }

    const open = (kind: PlanDialog['kind']) => () => {
      capture(ANALYTICS_EVENTS.PLAN_SELECTED, {
        tier: plan.tier,
        interval: billingInterval,
        path: kind === 'switch' ? 'change_plan' : 'new_subscription',
        fromTier: screen === 'plan' && account ? account.tier : overview.tier,
      });
      setDialog({ kind, plan, interval: billingInterval });
    };

    if (screen === 'plan' && account) {
      const higher = isHigherTier(plan.tier, account.tier);
      const sameTier = plan.tier === account.tier;
      const label = sameTier
        ? billingInterval === 'year'
          ? 'Switch to annual'
          : 'Switch to monthly'
        : `Switch to ${plan.name}`;

      return { label, primary: higher, onClick: open('switch'), disabled: !account.subscriptionId };
    }

    return { label: `Choose ${plan.name}`, primary: true, onClick: open('checkout') };
  };

  return (
    <div>
      <h3 className="ui-serif m-0 mb-4.5 text-[24px]">Billing</h3>

      {screen === 'legacy' ? (
        <LegacyPlanSection overview={overview} config={config} configError={configError} />
      ) : screen === 'plan' && account ? (
        <AccountPlanSection app={app} account={account} plans={plans} />
      ) : (
        <FreePlanCard freePlan={plans?.plans.find((p) => p.tier === 'free')} />
      )}

      <div className="mb-2.5 mt-6.5 flex items-center justify-between gap-3">
        <div className="ui-caption">Plans</div>
        {plans && (
          <BillingIntervalToggle
            interval={billingInterval}
            onChange={setBillingInterval}
            savingsPercent={savingsPercent}
          />
        )}
      </div>

      {plansQuery.isLoading ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(148px,1fr))' }}>
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </div>
      ) : plansQuery.isError || !plans ? (
        <PlansGridError onRetry={() => plansQuery.refetch()} />
      ) : (
        <>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(148px,1fr))' }}>
            {plansAtInterval(plans.plans, billingInterval).map((plan) => {
              const isCurrent =
                screen === 'plan' && account
                  ? plan.tier === account.tier && billingInterval === account.interval
                  : screen === 'free' && plan.tier === 'free';
              const isScheduled = screen === 'plan' && !!account && account.scheduledPlan === plan.tier;

              return (
                <PlanGridCard
                  key={plan.tier}
                  plan={plan}
                  interval={billingInterval}
                  isCurrent={isCurrent}
                  isScheduled={isScheduled}
                  action={isCurrent || isScheduled ? null : actionFor(plan)}
                />
              );
            })}
          </div>
          <div className="mt-2 text-[11px] text-muted">
            Pay once, use it on both: one plan and one credit balance for the website builder and Dunea Code.{' '}
            {screen === 'plan'
              ? 'Upgrades are charged the prorated difference today. Downgrades take effect at your next renewal.'
              : 'Annual plans refill credits every month.'}
          </div>
        </>
      )}

      <InviteBusinessCard />

      {dialog?.kind === 'checkout' && (
        <PlanCheckoutModal
          plan={dialog.plan}
          interval={dialog.interval}
          savingsPercent={savingsPercent}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'switch' && (
        <PlanSwitchModal
          plan={dialog.plan}
          interval={dialog.interval}
          app={app}
          currentName={currentName}
          onClose={() => setDialog(null)}
          onSwitched={(result) => {
            setDialog(null);
            queryClient.invalidateQueries({ queryKey: ['account-plan-overview'] });
            pollBillingState(queryClient);

            if (result.kind === 'upgraded') {
              notify.success(`You're on ${dialog.plan.name} now`);
            } else {
              notify.success(`${dialog.plan.name} starts on ${fmtDate(result.effectiveDate)}`);
            }
          }}
        />
      )}
    </div>
  );
}
